import { create } from 'zustand'
import {
  readAppearanceHint,
  resolveAppearance,
  writeAppearanceHint,
  type AppearancePreference,
  type ResolvedAppearance,
} from '../lib/appearance'

export const APPEARANCE_ENABLED = import.meta.env.VITE_APPEARANCE_ENABLED === 'true'

function systemIsDark(): boolean {
  return typeof window !== 'undefined' && window.matchMedia?.('(prefers-color-scheme: dark)').matches === true
}

function apply(preference: AppearancePreference): ResolvedAppearance {
  const resolved = APPEARANCE_ENABLED ? resolveAppearance(preference, systemIsDark()) : 'light'
  if (typeof document !== 'undefined') {
    document.documentElement.dataset.theme = resolved
    document.documentElement.style.colorScheme = resolved
  }
  return resolved
}

type AppearanceStore = {
  preference: AppearancePreference
  resolved: ResolvedAppearance
  accountUserId: string | null
  sessionEpoch: number
  saving: boolean
  error: 'save_failed' | 'sign_in_required' | null
  setPreference: (preference: AppearancePreference) => void
  hydrateFromAccount: (userId: string, preference: AppearancePreference) => void
  clearAccountAppearance: () => void
}

const initialPreference = APPEARANCE_ENABLED ? readAppearanceHint() : 'system'

export const useAppearanceStore = create<AppearanceStore>((set, get) => ({
  preference: initialPreference,
  resolved: APPEARANCE_ENABLED ? resolveAppearance(initialPreference, systemIsDark()) : 'light',
  accountUserId: null,
  sessionEpoch: 0,
  saving: false,
  error: null,
  setPreference: (preference) => {
    if (!APPEARANCE_ENABLED) return
    writeAppearanceHint(preference)
    set({ preference, resolved: apply(preference) })
  },
  hydrateFromAccount: (accountUserId, preference) => {
    if (get().accountUserId === accountUserId && get().saving) return
    if (APPEARANCE_ENABLED) writeAppearanceHint(preference)
    set((state) => ({
      accountUserId,
      sessionEpoch: state.accountUserId === accountUserId ? state.sessionEpoch : state.sessionEpoch + 1,
      preference,
      resolved: apply(preference),
      saving: false,
      error: null,
    }))
  },
  clearAccountAppearance: () => {
    writeAppearanceHint('system')
    set((state) => ({
      accountUserId: null,
      sessionEpoch: state.sessionEpoch + 1,
      preference: 'system',
      resolved: apply('system'),
      saving: false,
      error: null,
    }))
  },
}))

export function startAppearanceRuntime(): () => void {
  apply(useAppearanceStore.getState().preference)
  if (!APPEARANCE_ENABLED || typeof window === 'undefined') return () => {}

  const media = window.matchMedia?.('(prefers-color-scheme: dark)')
  const onSystemChange = () => {
    const preference = useAppearanceStore.getState().preference
    useAppearanceStore.setState({ resolved: apply(preference) })
  }
  const onStorage = (event: StorageEvent) => {
    if (event.key !== 'pexify_appearance' || useAppearanceStore.getState().accountUserId) return
    const preference = readAppearanceHint()
    useAppearanceStore.setState({ preference, resolved: apply(preference) })
  }
  media?.addEventListener('change', onSystemChange)
  window.addEventListener('storage', onStorage)
  return () => {
    media?.removeEventListener('change', onSystemChange)
    window.removeEventListener('storage', onStorage)
  }
}
